import { getStoreFromLoggerContext } from './async-logger-context';
import { writeToConsole } from './console';
import {
    type DebugLogger,
    type DebugLoggerFn,
    type DebugLoggerOptions,
    type DebugLogLabels,
    type DebugLogLevel,
    LOG_LEVELS,
} from './types';

/**
 * Create a console logger.
 */
export function Logger(namespace = 'default', options: DebugLoggerOptions = {}): DebugLogger {
    const globalLabels = options.labels || {};

    return {
        info: createLogMethod(namespace, 'info', globalLabels, options),
        notice: createLogMethod(namespace, 'notice', globalLabels, options),
        error: createLogMethod(namespace, 'error', globalLabels, options),
        warning: createLogMethod(namespace, 'warning', globalLabels, options),
        debug: createLogMethod(namespace, 'debug', globalLabels, options),
        critical: createLogMethod(namespace, 'critical', globalLabels, options),
        emergency: createLogMethod(namespace, 'emergency', globalLabels, options),
        alert: createLogMethod(namespace, 'alert', globalLabels, options),
        setLabels: (labels) => {
            Object.assign(globalLabels, labels);
        },
    };
}

function checkIsLogLevel(level: string): level is DebugLogLevel {
    return LOG_LEVELS.includes(level as DebugLogLevel);
}

function shouldLog(namespace: string, level: DebugLogLevel, enableForTests = false) {
    if (process.env.NODE_ENV === 'test' && !enableForTests) {
        return false;
    }

    const logLevel = process.env.GITBOOK_LOG_LEVEL;
    if (logLevel && checkIsLogLevel(logLevel)) {
        return LOG_LEVELS.indexOf(level) >= LOG_LEVELS.indexOf(logLevel);
    }

    if (level === 'debug' && process.env.NODE_ENV !== 'development') {
        const debugEnv = process.env.GITBOOK_DEBUG;
        if (!debugEnv || debugEnv === '*') {
            return false;
        }
    }

    if (LOG_LEVELS.indexOf(level) >= LOG_LEVELS.indexOf('error')) {
        return true;
    }

    if (!process.env.GITBOOK_DEBUG || process.env.GITBOOK_DEBUG === '*') {
        return true;
    }

    return namespace.startsWith(process.env.GITBOOK_DEBUG);
}

function createLogMethod(
    namespace: string,
    level: DebugLogLevel,
    labels: DebugLogLabels,
    options: DebugLoggerOptions
): DebugLoggerFn {
    const enabled = shouldLog(namespace, level, options.enableForTests);

    // @ts-ignore function is still incomplete
    const log: DebugLoggerFn = enabled
        ? (message: string, ...args: any) => {
              writeToConsole(namespace, level, {
                  message: toMessage(message, options.prefix),
                  args,
                  labels: {
                      ...Object.fromEntries(getStoreFromLoggerContext()?.entries() ?? []),
                      ...labels,
                  },
              });
          }
        : () => {};

    log.stream = {
        write: (message: string) => {
            if (!enabled) {
                return;
            }
            writeToConsole(namespace, level, {
                message: toMessage(message, options.prefix),
                labels,
                args: [],
            });
        },
    };

    return log;
}

function toMessage(message: string, prefix?: string): string {
    if (prefix) {
        return `${prefix} ${message}`;
    }

    return message;
}
